import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Chip, Container, Divider, FormControlLabel, IconButton, MenuItem, Paper,
  Select, Snackbar, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import arrowBackFill from '@iconify/icons-eva/arrow-back-fill';
import saveFill from '@iconify/icons-eva/save-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import emailFill from '@iconify/icons-eva/email-fill';
import linkedinFill from '@iconify/icons-eva/linkedin-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import Page from '../Page';
import PageHeader from './shared/PageHeader';
import StatusChip from './shared/StatusChip';
import RuleGroupBuilder from './RuleGroupBuilder';
import HtmlEditor from './shared/HtmlEditor';
import {
  getCampaign, updateCampaign, previewEligibility,
  listMessageTemplates, listOutlookAccounts, listUnipileAccounts, listActiveUsers,
} from '../../utils/outreachApi';

const TIMEZONES = ['Asia/Kolkata', 'Asia/Singapore', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'UTC'];
const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const CHANNELS = [
  { k: 'EMAIL',           l: 'Email',            color: '#1976d2', icon: emailFill    },
  { k: 'LINKEDIN_INVITE', l: 'LinkedIn invite',  color: '#0A66C2', icon: linkedinFill },
  { k: 'LINKEDIN_MESSAGE',l: 'LinkedIn message', color: '#0A66C2', icon: linkedinFill },
];
const channelMeta = (k) => CHANNELS.find((c) => c.k === k) || CHANNELS[0];
const fmtDelay = (sec) => {
  if (!sec) return 'Send immediately';
  const d = Math.round(sec / 86400);
  if (d >= 1) return `+${d} day${d > 1 ? 's' : ''}`;
  return `+${Math.round(sec / 3600)}h`;
};

// ─── SECTION WRAPPER ────────────────────────────────────────
const Section = ({ title, hint, children, action }) => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
          {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
        </Box>
        {action}
      </Stack>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Card>
);

// ─── AUDIENCE SECTION ───────────────────────────────────────
const AudienceSection = ({ draft, onChange }) => {
  useEffect(() => {
    if (!draft.rules?.length) return;
    const t = setTimeout(() => {
      previewEligibility(draft.rules).then(({ data }) => {
        onChange({ audience_match_count: data.count, audience_sample: data.sample });
      });
    }, 400);
    return () => clearTimeout(t);
  }, [JSON.stringify(draft.rules)]);

  return (
    <Section title="Audience"
             hint="Companies that match these rules are enrolled — including new matches added later (rolling).">
      <RuleGroupBuilder rules={draft.rules || []}
                        onChange={(next) => onChange({ rules: next })} />
      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap">
          <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700 }}>
            {(draft.audience_match_count || 0).toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            companies match right now
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Icon icon={refreshFill} width={13} color="#1976d2" />
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
              rolling enrollment
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Section>
  );
};

// ─── SEQUENCE SECTION ───────────────────────────────────────
const SequenceSection = ({ draft, onChange, templates }) => {
  const steps = draft.steps || [];
  const tplName = (id) => templates.find((t) => t.id === id)?.name || '— pick a template —';

  const updateStep = (i, patch) => {
    const next = [...steps];
    next[i] = { ...next[i], ...patch };
    onChange({ steps: next.map((s, idx) => ({ ...s, order: idx + 1 })) });
  };
  const addStep = (channel = 'EMAIL') => {
    const newStep = {
      order: steps.length + 1, channel, message_template_id: null,
      delay_seconds: steps.length === 0 ? 0 : 172800, stop_on_reply: true,
      skip_if_no_email: channel === 'EMAIL', skip_if_no_linkedin: channel.startsWith('LINKEDIN'),
    };
    onChange({ steps: [...steps, newStep] });
  };
  const removeStep = (i) => {
    onChange({ steps: steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx + 1 })) });
  };

  return (
    <Section title="Sequence"
             hint="Steps run one at a time. A reply automatically stops the sequence."
             action={
               <Button size="small" startIcon={<Icon icon={plusFill} />}
                       onClick={() => addStep('EMAIL')}>
                 Add step
               </Button>
             }>
      {steps.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            No steps yet.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button size="small" variant="outlined" onClick={() => addStep('EMAIL')}>+ Email</Button>
            <Button size="small" variant="outlined" onClick={() => addStep('LINKEDIN_INVITE')}>+ LinkedIn invite</Button>
            <Button size="small" variant="outlined" onClick={() => addStep('LINKEDIN_MESSAGE')}>+ LinkedIn message</Button>
          </Stack>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {steps.map((s, i) => {
            const meta = channelMeta(s.channel);
            const channelTemplates = templates.filter((t) => t.channel === s.channel);
            return (
              <Card key={i} variant="outlined"
                    sx={{ borderLeft: 4, borderLeftColor: meta.color, p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Box sx={{
                    width: 26, height: 26, borderRadius: '50%',
                    bgcolor: `${meta.color}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon icon={meta.icon} color={meta.color} width={14} />
                  </Box>
                  <Typography variant="caption" sx={{ color: meta.color, fontWeight: 700, letterSpacing: 0.6 }}>
                    STEP {i + 1}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <IconButton size="small" color="error" onClick={() => removeStep(i)}>
                    <Icon icon={trash2Fill} width={14} />
                  </IconButton>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">Channel</Typography>
                    <Select fullWidth size="small" value={s.channel}
                            onChange={(e) => updateStep(i, { channel: e.target.value, message_template_id: null })}>
                      {CHANNELS.map((c) => <MenuItem key={c.k} value={c.k}>{c.l}</MenuItem>)}
                    </Select>
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <Typography variant="caption" color="text.secondary">Template</Typography>
                    <Select fullWidth size="small" displayEmpty
                            value={s.message_template_id || ''}
                            onChange={(e) => updateStep(i, { message_template_id: e.target.value })}>
                      <MenuItem value="" disabled>{tplName(s.message_template_id)}</MenuItem>
                      {channelTemplates.map((tmpl) => (
                        <MenuItem key={tmpl.id} value={tmpl.id}>{tmpl.name}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="caption" color="text.secondary">Wait (days)</Typography>
                    <TextField type="number" size="small" fullWidth
                               value={Math.round((s.delay_seconds || 0) / 86400)}
                               onChange={(e) => updateStep(i, { delay_seconds: Number(e.target.value) * 86400 })}
                               helperText={fmtDelay(s.delay_seconds)} />
                  </Box>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}
    </Section>
  );
};

// ─── SENDERS SECTION ────────────────────────────────────────
// Single Outlook + single LinkedIn per campaign (decoupled from reviewers).
const SendersSection = ({ draft, onChange, outlook, unipile }) => {
  const outlookId = draft.outlook_connection || null;
  const unipileId = draft.unipile_account || null;
  const pickOutlook = (id) => onChange({ outlook_connection: outlookId === id ? null : id });
  const pickUnipile = (id) => onChange({ unipile_account:    unipileId === id ? null : id });
  const needsLi = (draft.steps || []).some((s) => s.channel?.startsWith('LINKEDIN'));

  return (
    <Section title="Sender accounts"
             hint="One Outlook + one LinkedIn account per campaign. Reviewers are picked separately.">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {/* Outlook (single) */}
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Icon icon={emailFill} width={16} color="#1976d2" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Outlook (sender mailbox)</Typography>
            <Chip size="small" variant="outlined" label={outlookId ? 'selected' : 'none'} />
          </Stack>
          <Stack spacing={0.75}>
            {outlook.map((o) => {
              const blocked = o.health === 'token_expired';
              const sel = outlookId === o.id;
              return (
                <Card key={o.id} variant="outlined"
                      onClick={() => !blocked && pickOutlook(o.id)}
                      sx={{
                        p: 1.25, cursor: blocked ? 'not-allowed' : 'pointer',
                        bgcolor: sel ? 'primary.lighter' : 'background.paper',
                        borderColor: sel ? 'primary.main' : 'grey.300',
                        opacity: blocked ? 0.6 : 1,
                      }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.user?.name || o.user_name || o.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{o.email}</Typography>
                    </Box>
                    <StatusChip status={o.health} />
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </Box>

        {/* LinkedIn (single) */}
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Icon icon={linkedinFill} width={16} color="#0A66C2" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>LinkedIn (sender profile)</Typography>
            <Chip size="small" variant="outlined" label={unipileId ? 'selected' : 'none'} />
            {!needsLi && <Chip size="small" label="not used" />}
          </Stack>
          <Stack spacing={0.75}>
            {unipile.map((u) => {
              const sel = unipileId === u.id;
              return (
                <Card key={u.id} variant="outlined"
                      onClick={() => pickUnipile(u.id)}
                      sx={{
                        p: 1.25, cursor: 'pointer',
                        bgcolor: sel ? 'primary.lighter' : 'background.paper',
                        borderColor: sel ? 'primary.main' : 'grey.300',
                      }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.linkedin_handle || u.profile_name || u.display_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        LinkedIn sender{u.display_name && u.display_name !== u.linkedin_handle ? ` · added by ${u.display_name}` : ''}
                      </Typography>
                    </Box>
                    <Typography variant="caption"
                                color={u.actions_today >= u.daily_action_cap * 0.9 ? 'warning.main' : 'text.secondary'}>
                      {u.actions_today}/{u.daily_action_cap}
                    </Typography>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </Box>
      </Stack>

      {needsLi && !unipileId && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Sequence has LinkedIn steps but no LinkedIn profile is selected — they'll skip on send.
        </Alert>
      )}
    </Section>
  );
};


// ─── REVIEWERS SECTION ──────────────────────────────────────
// Users-only multi-select. Reviewers approve queue entries + manage replies.
const ReviewersSection = ({ draft, onChange, activeUsers }) => {
  const reviewers = draft.reviewers || [];
  const toggle = (userId) => {
    const exists = reviewers.find((r) => r.user === userId);
    if (exists) {
      onChange({ reviewers: reviewers.filter((r) => r.user !== userId) });
    } else {
      onChange({ reviewers: [...reviewers, { user: userId, weight: 1, is_active: true }] });
    }
  };
  const setWeight = (userId, w) => {
    onChange({
      reviewers: reviewers.map((r) =>
        r.user === userId ? { ...r, weight: Math.max(1, Number(w) || 1) } : r
      ),
    });
  };

  return (
    <Section title="Reviewers"
             hint="Users who approve queue entries + manage reply threads. Round-robined per company (weighted).">
      {(activeUsers || []).length === 0 ? (
        <Alert severity="warning">No active users found.</Alert>
      ) : (
        <Stack spacing={0.75}>
          {activeUsers.map((u) => {
            const r = reviewers.find((x) => x.user === u.id);
            const sel = !!r;
            return (
              <Card key={u.id} variant="outlined"
                    onClick={() => toggle(u.id)}
                    sx={{
                      p: 1.25, cursor: 'pointer',
                      bgcolor: sel ? 'primary.lighter' : 'background.paper',
                      borderColor: sel ? 'primary.main' : 'grey.300',
                    }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.name || u.email}</Typography>
                    <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                  </Box>
                  {sel && (
                    <Stack direction="row" alignItems="center" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                      <Typography variant="caption" color="text.secondary">Weight</Typography>
                      <TextField type="number" size="small"
                                 value={r.weight}
                                 onChange={(e) => setWeight(u.id, e.target.value)}
                                 inputProps={{ min: 1, max: 10, style: { width: 40, padding: '4px 6px', textAlign: 'center' } }}
                                 sx={{ '& .MuiOutlinedInput-root': { height: 28 } }} />
                    </Stack>
                  )}
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}
      {reviewers.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {reviewers.length} reviewer{reviewers.length > 1 ? 's' : ''} assigned.
        </Typography>
      )}
    </Section>
  );
};

// ─── SCHEDULE SECTION ───────────────────────────────────────
const ScheduleSection = ({ draft, onChange }) => {
  const sched = draft.schedule || {};
  const update = (patch) => onChange({ schedule: { ...sched, ...patch } });

  return (
    <Section title="Schedule & limits"
             hint="Sends only happen inside the window on selected weekdays.">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Start</Typography>
          <TextField type="time" size="small" sx={{ minWidth: 130 }}
                     value={sched.window_start || '09:00'}
                     onChange={(e) => update({ window_start: e.target.value })} />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">End</Typography>
          <TextField type="time" size="small" sx={{ minWidth: 130 }}
                     value={sched.window_end || '18:00'}
                     onChange={(e) => update({ window_end: e.target.value })} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="caption" color="text.secondary">Timezone</Typography>
          <Select size="small" fullWidth value={sched.timezone || 'Asia/Kolkata'}
                  onChange={(e) => update({ timezone: e.target.value })}>
            {TIMEZONES.map((tz) => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
          </Select>
        </Box>
      </Stack>

      <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Weekdays
        </Typography>
        <ToggleButtonGroup size="small" value={sched.weekdays || []}
                           onChange={(_, next) => update({ weekdays: next })}
                           sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {DAYS.map((d) => (
            <ToggleButton key={d} value={d} sx={{ flex: 1, minWidth: 56 }}>{d}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Per-BA daily cap</Typography>
          <TextField type="number" size="small" fullWidth
                     value={sched.per_pm_daily_cap || 50}
                     onChange={(e) => update({ per_pm_daily_cap: Number(e.target.value) })} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Total cap (0 = unlimited)</Typography>
          <TextField type="number" size="small" fullWidth
                     value={sched.daily_send_cap || 0}
                     onChange={(e) => update({ daily_send_cap: Number(e.target.value) })} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Min gap (sec)</Typography>
          <TextField type="number" size="small" fullWidth
                     value={sched.min_step_gap_seconds || 30}
                     onChange={(e) => update({ min_step_gap_seconds: Number(e.target.value) })} />
        </Box>
      </Stack>
    </Section>
  );
};

// ─── MAIN ────────────────────────────────────────────────────
// ─── MESSAGE TEMPLATES (template-flow campaigns) ────────────
// Template-flow campaigns store their shared copy inline in
// campaign.template_messages (NOT MessageTemplate rows). The builder/chat
// wizard writes it at creation; this section lets it be edited afterward.
const TM_DEFS = [
  { key: 'email1',          label: 'Step 1 — Cold intro email',         hasSubject: true  },
  { key: 'linkedin_invite', label: 'Step 2 — LinkedIn connection note', hasSubject: false },
  { key: 'email_followup',  label: 'Step 3 — Follow-up email',          hasSubject: true  },
];

const TemplateMessagesSection = ({ draft, onChange }) => {
  if (draft.flow_type !== 'template') return null;   // AI flow generates per-company
  const tm = draft.template_messages || {};
  const setSlot = (key, patch) => {
    const next = { ...(draft.template_messages || {}) };
    next[key] = { ...(next[key] || {}), ...patch };
    onChange({ template_messages: next });
  };
  return (
    <Section
      title="Message templates"
      hint="Shared copy sent to every company in this campaign. Variables: {{first_name}}, {{company_name}}, {{pm_name}}. Editing here updates the templates — re-render existing review entries to apply."
    >
      <Stack spacing={3}>
        {TM_DEFS.map(({ key, label, hasSubject }) => {
          const slot = tm[key] || {};
          return (
            <Box key={key}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>{label}</Typography>
              {hasSubject && (
                <TextField fullWidth size="small" label="Subject" sx={{ mb: 1 }}
                           value={slot.subject || ''}
                           onChange={(e) => setSlot(key, { subject: e.target.value })} />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Body</Typography>
              <HtmlEditor value={slot.body || ''}
                          onChange={(html) => setSlot(key, { body: html })}
                          minHeight={hasSubject ? 160 : 110} />
            </Box>
          );
        })}
      </Stack>
    </Section>
  );
};

const CampaignEditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [outlook, setOutlook] = useState([]);
  const [unipile, setUnipile] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [snack, setSnack] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      getCampaign(id),
      listMessageTemplates(),
      listOutlookAccounts(),
      listUnipileAccounts(),
      listActiveUsers(),
    ]).then(([c, t, o, u, au]) => {
      const data = c.data || {};
      setDraft({
        ...data,
        // Decoupled sender/reviewer model. Existing reviewers come back from
        // the API as [{id, user, user_name, weight, is_active}, ...] — flatten
        // to the minimal write shape the save endpoint expects.
        outlook_connection: data.outlook_connection || null,
        unipile_account:    data.unipile_account || null,
        reviewers: (data.reviewers || []).map((r) => ({
          user: r.user, weight: r.weight || 1, is_active: r.is_active !== false,
        })),
      });
      setTemplates(t.data); setOutlook(o.data); setUnipile(u.data);
      setActiveUsers(au.data || []);
    });
  }, [id]);

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const sched = draft.schedule || {};
      const payload = {
        name:        draft.name,
        description: draft.description,
        // Sender accounts — single each.
        outlook_connection: draft.outlook_connection || null,
        unipile_account:    draft.unipile_account || null,
        // Reviewers — users-only, multi.
        reviewers: (draft.reviewers || []).map((r) => ({
          user: r.user, weight: r.weight || 1, is_active: r.is_active !== false,
        })),
        rules: (draft.rules || []).map(({ group, field, operator, value }) => ({
          group: group || 1, field, operator, value,
        })),
        steps: (draft.steps || []).map((s, i) => ({
          order:               i + 1,
          channel:             s.channel,
          message_template_id: s.message_template_id || null,
          delay_seconds:       s.delay_seconds || 0,
          stop_on_reply:       s.stop_on_reply !== false,
          skip_if_no_email:    s.skip_if_no_email || false,
          skip_if_no_linkedin: s.skip_if_no_linkedin || false,
        })),
        // Inline template copy — only relevant for template-flow campaigns.
        ...(draft.flow_type === 'template'
          ? { template_messages: draft.template_messages || {} }
          : {}),
        schedule_window_start:  sched.window_start  || '09:00',
        schedule_window_end:    sched.window_end    || '18:00',
        schedule_timezone:      sched.timezone      || 'Asia/Kolkata',
        schedule_weekdays:      sched.weekdays      || [],
        daily_send_cap:         sched.daily_send_cap      ?? 0,
        per_pm_daily_cap:       sched.per_pm_daily_cap    ?? 50,
        min_step_gap_seconds:   sched.min_step_gap_seconds ?? 30,
      };
      await updateCampaign(id, payload);
      setSnack({ severity: 'success', message: 'Campaign saved.' });
    } catch {
      setSnack({ severity: 'error', message: 'Save failed — check the console for details.' });
    }
    setSaving(false);
  };

  if (!draft) return (
    <Page title="Edit campaign | CRM">
      <Container sx={{ py: 4 }}>
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      </Container>
    </Page>
  );

  return (
    <Page title={`Edit ${draft.name} | CRM`}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <PageHeader
          title={`Edit · ${draft.name}`}
          subtitle="Update audience, sequence, senders, or schedule. Changes apply to future enrollments."
          actions={
            <Stack direction="row" spacing={1.5} alignItems="center">
              <StatusChip status={draft.status} />
              <Button startIcon={<Icon icon={arrowBackFill} />}
                      onClick={() => navigate('/outreach/campaigns')}>
                Back
              </Button>
              <Button variant="contained"
                      startIcon={<Icon icon={saveFill} />}
                      disabled={saving} onClick={save}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </Stack>
          }
        />

        {/* Basics */}
        <Section title="Basics">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label="Campaign name" size="small" sx={{ flex: 1, minWidth: 260 }}
                       value={draft.name || ''}
                       onChange={(e) => update({ name: e.target.value })} />
            <TextField label="Description" size="small" sx={{ flex: 2 }}
                       value={draft.description || ''}
                       onChange={(e) => update({ description: e.target.value })} />
          </Stack>
        </Section>

        <AudienceSection draft={draft} onChange={update} />
        <SequenceSection draft={draft} onChange={update} templates={templates} />
        <TemplateMessagesSection draft={draft} onChange={update} />
        <SendersSection draft={draft} onChange={update} outlook={outlook} unipile={unipile} />
        <ReviewersSection draft={draft} onChange={update} activeUsers={activeUsers} />
        <ScheduleSection draft={draft} onChange={update} />

        <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)}>
          {snack && <Alert severity={snack.severity}>{snack.message}</Alert>}
        </Snackbar>
      </Container>
    </Page>
  );
};

export default CampaignEditForm;
